import { AssignmentDetails } from 'century-core/core-apis/assignment/types';
import { MixpanelParams } from 'century-core/core-utils/utils/mixpanel/mixpanel';
import { MixpanelEventTypes } from 'century-core/core-utils/utils/mixpanel/MixpanelEventTypes';
import { MixpanelKeys } from 'century-core/core-utils/utils/mixpanel/MixpanelKeys';
import mixpanel from 'mixpanel-browser';

export const useAssignmentMixpanel = () => {
  return (eventName: MixpanelEventTypes, assignment: AssignmentDetails, options?: MixpanelParams) => {
    mixpanel.track(eventName, {
      [MixpanelKeys.AssignmentId]: assignment.assignmentId,
      [MixpanelKeys.AssignmentName]: assignment.name,
      [MixpanelKeys.AssignmentStatus]: assignment.status,
      [MixpanelKeys.AssignedNuggets]: assignment.nuggets.length,
      [MixpanelKeys.Kind]: assignment.kind,
      [MixpanelKeys.Group]: assignment.groups.length,
      [MixpanelKeys.AssignedSmartNuggets]: assignment.smartRecommendationSettings?.nuggetCount || 'None assigned',
      ...options,
    });
  };
};

/** Class and student groups */
export type CreateStep = 'select group type' | 'select classes' | 'select students' | 'enter settings';
export type GroupType = 'classes' | 'students' | 'skip';

export const useSubmitCreateAssignmentMixpanel = () => {
  const track = useAssignmentMixpanel();
  return (assignment: AssignmentDetails, options: MixpanelParams) => {
    track(MixpanelEventTypes.TeacherDashboardCreateAssignmentGroupModal, assignment, {
      [MixpanelKeys.ModalAction]: 'submitted',
      ...options,
    });
  };
};

export const useDismissCreateAssignmentMixpanel = () => {
  return (options?: MixpanelParams) => {
    mixpanel.track(MixpanelEventTypes.TeacherDashboardCreateAssignmentGroupModal, {
      [MixpanelKeys.ModalAction]: 'dismissed',
      ...options,
    });
  };
};

export const useAddAssignmentGroupMixpanel = (assignment: AssignmentDetails, groupType: Omit<GroupType, 'skip'>) => {
  const track = useAssignmentMixpanel();
  return (action: 'dismissed' | 'added', ids: string[]) => {
    track(MixpanelEventTypes.TeacherDashboardAssignmentAddGroup, assignment, {
      [MixpanelKeys.ModalAction]: action,
      [MixpanelKeys.GroupType]: groupType,
      [MixpanelKeys.GroupId]: ids,
      [MixpanelKeys.GroupAmount]: ids.length,
    });
  };
};

export const useRemoveAssignmentGroupMixpanel = (
  assignment: AssignmentDetails,
  groupType: Omit<GroupType, 'skip'>,
  assignmentGroupId: string
) => {
  const track = useAssignmentMixpanel();

  return (action: 'dismissed' | 'removed', options?: MixpanelParams) => {
    track(MixpanelEventTypes.TeacherDashboardAssignmentRemoveGroup, assignment, {
      [MixpanelKeys.ModalAction]: action,
      [MixpanelKeys.GroupType]: groupType,
      [MixpanelKeys.GroupId]: assignmentGroupId,
      ...options,
    });
  };
};

export const useAssignmentGroupClassClickedMixpanel = (assignment: AssignmentDetails) => {
  const track = useAssignmentMixpanel();
  return (groupId: string, classId: string, groupType: Omit<GroupType, 'skip'>) => {
    track(MixpanelEventTypes.TeacherDashboardAssignmentsClassesWidgetItemClicked, assignment, {
      [MixpanelKeys.GroupId]: groupId,
      [MixpanelKeys.ClassId]: classId,
      [MixpanelKeys.GroupType]: groupType,
    });
  };
};

export const useAssignmentGroupStudentClickedMixpanel = (assignment: AssignmentDetails) => {
  const track = useAssignmentMixpanel();
  return (groupId: string, groupType: Omit<GroupType, 'skip'>) => {
    track(MixpanelEventTypes.TeacherDashboardAssignmentsStudentGroupWidgetItemClicked, assignment, {
      [MixpanelKeys.GroupId]: groupId,
      [MixpanelKeys.GroupType]: groupType,
    });
  };
};

/** Smart Assignments */
export type SmartModalName = 'missing data alert modal' | 'smart nuggets modal';
export type SmartUpdateType = 'add' | 'edit';

export const useSmartNuggetsModalMixpanel = (assignment: AssignmentDetails) => {
  const track = useAssignmentMixpanel();

  return (modalName: SmartModalName, action: 'dismissed' | 'submitted', options: MixpanelParams) => {
    track(MixpanelEventTypes.TeacherDashboardAssignmentSettingsSmartNuggetsModal, assignment, {
      [MixpanelKeys.ModalAction]: action,
      [MixpanelKeys.ModalName]: modalName,
      ...options,
    });
  };
};

type CourseMissingType = 'class' | 'individual-student' | 'multiple-students';
export const useCourseMissingModalMixpanel = (assignment: AssignmentDetails, groupId: string, type: CourseMissingType) => {
  const track = useAssignmentMixpanel();
  return (action: 'dismissed' | 'removed', options?: MixpanelParams) => {
    track(MixpanelEventTypes.TeacherDashboardAssignmentSettingsCourseMissingModal, assignment, {
      [MixpanelKeys.ModalAction]: action,
      [MixpanelKeys.CourseMissingType]: type,
      [MixpanelKeys.GroupId]: groupId,
      ...options,
    });
  };
};

export const useCourseMissingModalGoToButtonMixpanel = (assignment: AssignmentDetails, groupId: string, type: CourseMissingType) => {
  const track = useAssignmentMixpanel();
  return () => {
    track(MixpanelEventTypes.TeacherDashboardAssignmentSettingsGoToButtonClicked, assignment, {
      [MixpanelKeys.CourseMissingType]: type,
      [MixpanelKeys.GroupId]: groupId,
    });
  };
};

export const useRemoveAssignmentSmartNuggetsMixpanel = (assignment: AssignmentDetails) => {
  const track = useAssignmentMixpanel();
  return (action: 'dismissed' | 'removed') => {
    track(MixpanelEventTypes.TeacherDashboardAssignmentRemoveSmartNuggets, assignment, {
      [MixpanelKeys.ModalAction]: action,
    });
  };
};

export const useCopyAssignmentLinkMixpanel = (assignment: AssignmentDetails) => {
  const track = useAssignmentMixpanel();
  return () => {
    track(MixpanelEventTypes.TeacherDashboardAssignmentCopyLink, assignment);
  };
};
